import type { UsageSummary } from '@rp/contracts'
import { useEffect, useRef, useState } from 'react'
import { Card, StatusBar, type StatusTone } from '../../design'
import { formatDateTime, LOCALE } from '../../lib/time'
import { ADJUST_BUDGET_BUTTON_ID, AdjustBudgetButton, BudgetForm } from './BudgetEditor'
import { USAGE_COPY } from './copy'
import { budgetPace, type BudgetPace } from './pace'
import { NEAR_LIMIT_RATIO } from './queries'

const numberFormat = new Intl.NumberFormat(LOCALE)
const PERCENT = 100
const NEAR_LIMIT_PERCENT = NEAR_LIMIT_RATIO * PERCENT

type Verdict = { tone: StatusTone; title: string; detail?: string; live: 'status' | 'alert' | 'none' }

const verdictOf = (pace: BudgetPace): Verdict => {
  if (pace.outlook === 'exhausted') return { tone: 'danger', title: USAGE_COPY.exhausted, live: 'alert' }
  if (pace.outlook === 'overspending')
    return {
      tone: 'warning',
      title: pace.exhaustsOn === null ? USAGE_COPY.overspendingTitleUndated : USAGE_COPY.overspendingTitle(pace.exhaustsOn),
      detail: USAGE_COPY.overspendingBody(numberFormat.format(pace.projectedTokens)),
      live: 'status',
    }
  if (pace.percentUsed >= NEAR_LIMIT_PERCENT) return { tone: 'warning', title: USAGE_COPY.nearLimit, live: 'status' }
  return {
    tone: 'success',
    title: USAGE_COPY.onTrackTitle,
    detail: USAGE_COPY.onTrackBody(numberFormat.format(pace.projectedTokens)),
    live: 'none',
  }
}

const BudgetMeter = ({ pace }: { pace: BudgetPace }) => (
  <span
    className="usage-meter"
    role="meter"
    aria-label={USAGE_COPY.meterLabel}
    aria-valuenow={pace.percentUsed}
    aria-valuemin={0}
    aria-valuemax={PERCENT}
    aria-valuetext={USAGE_COPY.usedRatio(pace.percentUsed)}
    data-outlook={pace.outlook}
  >
    <span className="usage-meter-fill" style={{ inlineSize: `${pace.percentUsed}%` }} />
    <span
      className="usage-meter-pace"
      aria-hidden="true"
      title={USAGE_COPY.paceMarkLabel(pace.percentElapsed)}
      style={{ insetInlineStart: `${pace.percentElapsed}%` }}
    />
  </span>
)

const Figure = ({ label, value }: { label: string; value: number }) => (
  <div className="usage-figure">
    <dt>{label}</dt>
    <dd className="num">{numberFormat.format(value)}</dd>
  </div>
)

export const BudgetPanel = ({ usage, now }: { usage: UsageSummary; now: Date }) => {
  const pace = budgetPace(usage, now)
  const verdict = verdictOf(pace)
  const [editingBudget, setEditingBudget] = useState(false)
  const wasEditing = useRef(false)

  useEffect(() => {
    if (wasEditing.current && !editingBudget) document.getElementById(ADJUST_BUDGET_BUTTON_ID)?.focus()
    wasEditing.current = editingBudget
  }, [editingBudget])

  return (
    <Card as="section" className="usage-card">
      <div className="usage-card-head">
        <h2>{USAGE_COPY.budgetHeading}</h2>
        <span className="usage-period num">{USAGE_COPY.period(usage.periodStart, usage.periodEnd)}</span>
      </div>

      <StatusBar
        tone={verdict.tone}
        title={verdict.title}
        live={verdict.live}
        {...(verdict.detail === undefined ? {} : { detail: verdict.detail })}
      />

      <div className="usage-meter-block">
        <BudgetMeter pace={pace} />
        <div className="usage-meter-legend">
          <span>{USAGE_COPY.usedRatio(pace.percentUsed)}</span>
          <span>{USAGE_COPY.elapsed(pace.daysElapsed, pace.daysInPeriod)}</span>
        </div>
      </div>

      <dl className="usage-figures">
        <Figure label={USAGE_COPY.used} value={usage.budget.tokensUsed} />
        <div className="usage-figure">
          <dt>{USAGE_COPY.limit}</dt>
          <dd className="num usage-limit">
            <span className="usage-limit-value">{numberFormat.format(usage.budget.monthlyTokenBudget)}</span>
            {editingBudget ? null : <AdjustBudgetButton onClick={() => setEditingBudget(true)} />}
          </dd>
        </div>
        <Figure label={USAGE_COPY.remaining} value={usage.budget.tokensRemaining} />
      </dl>

      {editingBudget ? (
        <BudgetForm
          key={usage.budget.monthlyTokenBudget}
          budget={usage.budget.monthlyTokenBudget}
          onClose={() => setEditingBudget(false)}
        />
      ) : null}

      <p className="usage-updated">
        {usage.updatedAt === null ? USAGE_COPY.neverUpdated : USAGE_COPY.updatedAt(formatDateTime(new Date(usage.updatedAt)))}
      </p>
    </Card>
  )
}
